/* global document */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useAuth } from '../../contexts/AuthContext';
import { useCollection, indexById } from '../../runtime/mobile/useCollection';
import { createAdminClient } from '../../runtime/mobile/adminClient';
import { TEST_WORKER_URL } from '../../runtime/mobile/testWorkerClient';
import { readableMobileError } from '../../runtime/mobile/employeeRuntimeState';
import { presenceStatus } from '../../runtime/presence';
import { colors, radius, spacing, typography } from '../../config/theme';
import { Avatar, Card, Chip, ErrorBanner, Field, Loader, PrimaryButton, SectionLabel, TextButton, Toggle, screen } from '../ui';
import { DEFAULT_DAY_RATE } from './exportHours';

const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?';

const inviteUrl = (result) => {
  if (result?.url) return result.url;
  const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
  return origin ? `${origin}/invite?t=${result.token}` : `…/invite?t=${result.token}`;
};

export default function EmployeeListScreen() {
  const { user } = useAuth();
  const users = useCollection('users');
  const locations = useCollection('locations');
  const client = useMemo(() => createAdminClient(user), [user]);
  const [presence, setPresence] = useState({});
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const token = await user.getIdToken();
        const r = await fetch(`${TEST_WORKER_URL}/presence`, {
          method: 'POST',
          headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
          body: '{}',
        });
        const j = await r.json();
        if (alive && j && j.presence) setPresence(j.presence);
      } catch { /* keep last */ }
    };
    load();
    const id = setInterval(() => { if (typeof document === 'undefined' || !document.hidden) load(); }, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);
  const [error, setError] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [invite, setInvite] = useState(null); // { url } once minted
  const [copied, setCopied] = useState(false);

  const makeInvite = async () => {
    setInviting(true);
    setError(null);
    setCopied(false);
    try {
      const result = await client.createInvite(null);
      setInvite({ url: inviteUrl(result), expiresAt: result.expiresAt });
    } catch (e) {
      setError(readableMobileError(e));
    } finally {
      setInviting(false);
    }
  };

  const copyInvite = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(invite.url);
        setCopied(true);
      }
    } catch { /* selection fallback below */ }
  };

  const locById = indexById(locations.docs);
  const groups = useMemo(() => {
    const map = new Map();
    const loose = [];
    for (const u of [...users.docs].sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''))) {
      const key = u.primaryLocationId || (u.locationIds || [])[0];
      if (key && locById[key]) { if (!map.has(key)) map.set(key, []); map.get(key).push(u); }
      else loose.push(u);
    }
    return { map, loose };
  }, [users.docs, locById]);

  if (users.loading && !users.docs.length) return <Loader />;

  const row = (u) => {
    const seen = presenceStatus(presence[u.id]);
    return (
      <Card key={u.id} onPress={() => setEditing(u)} style={styles.row}>
        <View>
          <Avatar label={initials(u.fullName)} kind={u.active ? 'teal' : 'muted'} />
          {seen?.online ? <View style={styles.onlineDot} /> : null}
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.name}>{u.fullName || u.email}</Text>
          <Text style={styles.meta}>
            {u.role === 'admin' ? 'администратор' : 'сотрудник'}
            {(u.locationIds || []).length > 1 ? ` · ${u.locationIds.length} точки` : ''}
            {!u.active ? ' · не активирован' : ''}
          </Text>
          {u.active && seen ? (
            <Text style={[styles.seen, seen.online && styles.seenOnline]}>
              {seen.online ? '● в сети' : `был(а) в сети ${seen.text}`}
            </Text>
          ) : null}
        </View>
        <Text style={styles.chevron}>›</Text>
      </Card>
    );
  };

  return (
    <View style={screen.root}>
      <ScrollView contentContainerStyle={screen.content}>
        <ErrorBanner message={error || users.error} />
        <PrimaryButton title="＋ Пригласить сотрудника" onPress={makeInvite} loading={inviting} />
        {groups.loose.length > 0 && (
          <View style={{ gap: spacing.sm }}>
            <SectionLabel>Новые — назначьте точку</SectionLabel>
            {groups.loose.map(row)}
          </View>
        )}
        {[...groups.map.entries()].map(([locId, list]) => (
          <View key={locId} style={{ gap: spacing.sm }}>
            <SectionLabel>{locById[locId]?.name || locId}</SectionLabel>
            {list.map(row)}
          </View>
        ))}
        <Text style={styles.footnote}>
          Пришлите сотруднику ссылку-приглашение. Он создаст аккаунт и появится здесь — откройте карточку, чтобы активировать доступ и назначить точку.
        </Text>
      </ScrollView>

      {invite && (
        <Modal visible animationType="fade" transparent onRequestClose={() => setInvite(null)}>
          <View style={styles.wrap}>
            <View style={styles.sheet}>
              <View style={styles.sheetHead}>
                <Text style={styles.sheetTitle}>Ссылка-приглашение</Text>
                <TextButton title="Готово" kind="muted" onPress={() => setInvite(null)} />
              </View>
              <Text style={styles.inviteHint}>
                Отправьте её сотруднику любым способом. Ссылка действует ограниченное время и создаёт один аккаунт (неактивный, пока вы не активируете).
              </Text>
              <Text selectable style={styles.inviteLink}>{invite.url}</Text>
              <PrimaryButton title={copied ? '✓ Скопировано' : 'Скопировать ссылку'} onPress={copyInvite} />
            </View>
          </View>
        </Modal>
      )}

      {editing && (
        <EditSheet
          key={editing.id}
          user={editing}
          locations={locations.docs.filter((l) => l.active || (editing.locationIds || []).includes(l.id))}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            try {
              await client.updateUser(editing.id, patch);
              setEditing(null);
            } catch (e) {
              setError(readableMobileError(e));
            }
          }}
        />
      )}
    </View>
  );
}

function EditSheet({ user, locations, onClose, onSave }) {
  const [active, setActive] = useState(!!user.active);
  const [isAdmin, setIsAdmin] = useState(user.role === 'admin');
  const [picked, setPicked] = useState(() => new Set(user.locationIds || []));
  const [primary, setPrimary] = useState(user.primaryLocationId || null);
  const [rateKind, setRateKind] = useState(user.payRate?.kind === 'perHour' ? 'perHour' : 'perDay');
  const [rateAmount, setRateAmount] = useState(
    user.payRate?.amount != null ? String(user.payRate.amount) : '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const toggleLoc = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); if (primary === id) setPrimary(null); }
      else next.add(id);
      return next;
    });
  };

  const save = () => {
    const locationIds = [...picked];
    if (active && !isAdmin && locationIds.length === 0) { setError('Для активного сотрудника нужна хотя бы одна точка'); return; }
    const primaryLocationId = primary && locationIds.includes(primary) ? primary : (locationIds[0] || null);
    const amt = Math.round(parseFloat(String(rateAmount).replace(',', '.')) || 0);
    if (rateAmount.trim() && (!(amt > 0) || amt > 1_000_000)) { setError('Ставка — число больше 0'); return; }
    const payRate = amt > 0 ? { kind: rateKind, amount: amt } : null;
    setBusy(true);
    setError(null);
    Promise.resolve(onSave({ active, role: isAdmin ? 'admin' : 'employee', locationIds, primaryLocationId, payRate }))
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  return (
    <Modal visible animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.wrap}>
        <View style={styles.sheet}>
          <View style={styles.sheetHead}>
            <Text style={styles.sheetTitle}>{user.fullName || user.email}</Text>
            <TextButton title="Отмена" kind="muted" onPress={onClose} />
          </View>
          <ScrollView contentContainerStyle={{ gap: spacing.md }} keyboardShouldPersistTaps="handled">
            <ErrorBanner message={error} />
            <Text style={styles.email}>{user.email}</Text>

            <Pressable onPress={() => setActive((v) => !v)} style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Доступ активен</Text>
              <Toggle value={active} onValueChange={setActive} />
            </Pressable>
            <Pressable onPress={() => setIsAdmin((v) => !v)} style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Администратор</Text>
              <Toggle value={isAdmin} onValueChange={setIsAdmin} />
            </Pressable>

            <Text style={styles.groupLabel}>ТОЧКИ</Text>
            {locations.map((l) => (
              <Pressable key={l.id} onPress={() => toggleLoc(l.id)} style={[styles.locRow, picked.has(l.id) && styles.locRowOn]}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.locName}>{l.name}</Text>
                  {picked.has(l.id) && (
                    <Pressable onPress={() => setPrimary(l.id)} hitSlop={6}>
                      <Text style={[styles.primaryLink, primary === l.id && styles.primaryLinkOn]}>
                        {primary === l.id ? '★ основная' : 'сделать основной'}
                      </Text>
                    </Pressable>
                  )}
                </View>
                {picked.has(l.id) ? <Chip label="выбрана" kind="good" /> : null}
              </Pressable>
            ))}

            <Text style={styles.groupLabel}>ОПЛАТА</Text>
            <View style={styles.rateKindRow}>
              {[['perDay', 'за смену'], ['perHour', 'за час']].map(([k, lbl]) => (
                <Pressable key={k} onPress={() => setRateKind(k)} style={[styles.rateChip, rateKind === k && styles.rateChipOn]}>
                  <Text style={[styles.rateChipText, rateKind === k && styles.rateChipTextOn]}>{lbl}</Text>
                </Pressable>
              ))}
            </View>
            <Field
              label={`Ставка, ₽ ${rateKind === 'perDay' ? 'за смену' : 'за час'}`}
              value={rateAmount}
              onChangeText={setRateAmount}
              keyboardType="number-pad"
              placeholder={rateKind === 'perDay' ? String(DEFAULT_DAY_RATE) : '—'}
            />
            <Text style={styles.rateHint}>
              Пусто → берётся общая ставка {DEFAULT_DAY_RATE} ₽ за смену. Влияет только на отчёт «к выплате».
            </Text>

            <PrimaryButton title="Сохранить" onPress={save} loading={busy} />
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  name: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 14 },
  meta: { ...typography.caption, color: colors.textMuted, marginTop: 1 },
  seen: { ...typography.caption, color: colors.textMuted, fontSize: 11, marginTop: 2 },
  seenOnline: { color: colors.success, fontWeight: '700' },
  onlineDot: {
    position: 'absolute', right: -1, bottom: -1, width: 11, height: 11, borderRadius: 6,
    backgroundColor: colors.success, borderWidth: 2, borderColor: colors.surfaceCard,
  },
  chevron: { fontSize: 22, color: colors.textMuted },
  footnote: { ...typography.caption, color: colors.textMuted, marginTop: spacing.md },
  wrap: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: colors.surface, borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg, padding: spacing.lg, maxHeight: '90%' },
  sheetHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  sheetTitle: { ...typography.h2, color: colors.textPrimary, flex: 1 },
  email: { ...typography.caption, color: colors.textMuted },
  inviteHint: { ...typography.caption, color: colors.textSecondary, marginBottom: spacing.md },
  inviteLink: { ...typography.caption, color: colors.textPrimary, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, padding: spacing.md, marginBottom: spacing.md },
  groupLabel: { ...typography.caption, color: colors.textMuted, fontWeight: '700', letterSpacing: 1, marginTop: spacing.xs },
  toggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.md },
  toggleLabel: { ...typography.body, color: colors.textPrimary },
  locRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.md },
  locRowOn: { borderColor: colors.primary },
  locName: { ...typography.body, color: colors.textPrimary, fontSize: 14 },
  primaryLink: { ...typography.caption, color: colors.textMuted, marginTop: 2 },
  primaryLinkOn: { color: colors.primary, fontWeight: '700' },
  rateKindRow: { flexDirection: 'row', gap: spacing.sm },
  rateChip: { flex: 1, alignItems: 'center', paddingVertical: spacing.sm, borderRadius: radius.sm, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border },
  rateChipOn: { borderColor: colors.primary, backgroundColor: 'rgba(237,42,35,0.08)' },
  rateChipText: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  rateChipTextOn: { color: colors.primary },
  rateHint: { ...typography.caption, color: colors.textMuted },
});
